import React, { useState } from 'react';

import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  createHeadingField,
  createSkillField,
  FormField,
  FormGroup,
} from '../../types/formSchema';
import Button from '../common/Button';
import { ChevronDownIcon, ChevronUpIcon } from '../common/ChevronIcons/ChevronIcons';
import ConfirmDialog from '../common/ConfirmDialog/ConfirmDialog';
import FormFieldEditor from './FormFieldEditor';

export interface FormGroupEditorProps {
  group: FormGroup;
  onUpdate: (updated: FormGroup) => void;
  onDelete: () => void;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  canEnableMoreVideos: boolean;
  onVideoLimitReached: () => void;
}

function reorder<T extends { order: number }>(items: T[]): T[] {
  return items.map((item, i) => ({ ...item, order: i }));
}

const FormGroupEditor: React.FC<FormGroupEditorProps> = ({
  group,
  onUpdate,
  onDelete,
  collapsed,
  onCollapsedChange,
  canEnableMoreVideos,
  onVideoLimitReached,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: group.id });

  const groupStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    position: 'relative',
    zIndex: isDragging ? 1 : 0,
  };

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onUpdate({ ...group, label: e.target.value });
  };

  const updateField = (fieldId: string, updated: FormField): void => {
    onUpdate({
      ...group,
      fields: group.fields.map((f) => (f.id === fieldId ? updated : f)),
    });
  };

  const deleteField = (fieldId: string): void => {
    onUpdate({
      ...group,
      fields: reorder(group.fields.filter((f) => f.id !== fieldId)),
    });
  };

  const addSkill = (): void => {
    onUpdate({
      ...group,
      fields: [...group.fields, createSkillField(group.fields.length)],
    });
  };

  const addCategory = (): void => {
    onUpdate({
      ...group,
      fields: [...group.fields, createHeadingField(group.fields.length)],
    });
  };

  const fieldIds = group.fields.map((f) => f.id);
  const groupNeedsName = !group.label.trim();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const sectionLabelTrimmed = group.label.trim();
  const groupBodyId = `fe-group-body-${group.id}`;

  return (
    <div ref={setNodeRef} style={groupStyle} className="fe-group-sortable">
      <div
        className={`fe-group${collapsed ? ' fe-group-collapsed' : ''}`}
      >
        <div className="fe-group-header">
          <button
            type="button"
            className="fe-drag-handle fe-group-drag-handle"
            {...listeners}
            {...attributes}
            aria-label="Drag to reorder group"
            title="Drag to reorder group"
          >
            <span className="fe-drag-handle-bars" aria-hidden />
          </button>
          <input
            className={`fe-group-label-input${groupNeedsName ? ' fe-input-needs-name' : ''}`}
            type="text"
            value={group.label}
            onChange={handleLabelChange}
            placeholder="Section name (e.g. Jumps, Tumbling)..."
            aria-label="Group name"
            aria-invalid={groupNeedsName}
          />
          <div className="fe-group-header-actions">
            <button
              type="button"
              className="fe-icon-btn fe-group-collapse-toggle"
              onClick={() => onCollapsedChange(!collapsed)}
              aria-expanded={!collapsed}
              aria-controls={groupBodyId}
              aria-label={collapsed ? 'Expand section' : 'Collapse section'}
              title={collapsed ? 'Expand section' : 'Collapse section'}
            >
              {collapsed ? (
                <ChevronDownIcon className="fe-group-collapse-chevron" />
              ) : (
                <ChevronUpIcon className="fe-group-collapse-chevron" />
              )}
            </button>
            <button
              type="button"
              className="fe-icon-btn fe-icon-btn-danger"
              onClick={() => setDeleteConfirmOpen(true)}
              aria-label="Delete group"
              title="Delete group"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M12.7 4.7l-1.4-1.4L8 6.6 4.7 3.3 3.3 4.7 6.6 8l-3.3 3.3 1.4 1.4L8 9.4l3.3 3.3 1.4-1.4L9.4 8l3.3-3.3z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>

        {!collapsed ? (
          <div id={groupBodyId}>
            {group.fields.length > 0 ? (
              <SortableContext
                items={fieldIds}
                strategy={verticalListSortingStrategy}
              >
                <div className="fe-fields-list">
                  {group.fields.map((field) => (
                    <FormFieldEditor
                      key={field.id}
                      field={field}
                      onUpdate={(updated) => updateField(field.id, updated)}
                      onDelete={() => deleteField(field.id)}
                      canEnableMoreVideos={canEnableMoreVideos}
                      onVideoLimitReached={onVideoLimitReached}
                    />
                  ))}
                </div>
              </SortableContext>
            ) : null}

            <div className="fe-group-add-row">
              <Button variant="secondary" compact onClick={addSkill}>
                + Skill
              </Button>
              <Button variant="secondary" compact onClick={addCategory}>
                + Category
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete section?"
        message={
          sectionLabelTrimmed
            ? `Are you sure you want to delete “${sectionLabelTrimmed}”?`
            : 'Are you sure you want to delete this section?'
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => {
          setDeleteConfirmOpen(false);
          onDelete();
        }}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </div>
  );
};

export default FormGroupEditor;
